// src/components/LetterheadSettings.js — Settings → Letterhead: the saved NJJBA letterhead
// [marker: finish-up v1]
import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const KINDS = {
  docx: { title: 'Letterhead (Word .docx)', accept: '.docx', hint: 'Your NJJBA letterhead with placeholders like {{First Name}}, {{Business Name}}, {{Address}}. Used for renewal letters.' },
  pdf: { title: 'Letterhead background (PDF, optional)', accept: '.pdf', hint: 'The same letterhead saved from Word as a PDF. It’s drawn behind every page of the bulk letters so logos and borders always print.' },
};

export default function LetterheadSettings({ canEdit = true }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(null);
  const refs = { docx: useRef(null), pdf: useRef(null) };
  useEffect(() => { api.get('/letterhead').then(r => setInfo(r.data)).catch(() => toast.error('Could not load the letterhead.')); }, []);

  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    setBusy(kind);
    try { const r = await api.post(`/letterhead/${kind}`, fd); setInfo(r.data); toast.success('Saved.'); }
    catch (err) { toast.error(err.response?.data?.error || 'Upload failed.'); }
    finally { setBusy(null); if (refs[kind].current) refs[kind].current.value = ''; }
  };
  const remove = async (kind) => {
    if (!window.confirm('Remove this file?')) return;
    setBusy(kind);
    try { const r = await api.delete(`/letterhead/${kind}`); setInfo(r.data); toast.success('Removed.'); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not remove.'); }
    finally { setBusy(null); }
  };
  const download = async (kind) => {
    try {
      const r = await api.get(`/letterhead/download/${kind}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = info[kind].name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error('Could not download.'); }
  };
  if (!info) return <div className="spinner" />;

  return (
    <div className="card">
      <div className="card-header"><span className="card-title">Letterhead</span></div>
      <div className="card-body">
        <p style={{ marginTop: 0, fontSize: '0.875rem', color: 'var(--gray-600)' }}>
          Save the NJJBA letterhead once and every new renewal batch uses it automatically — no need to upload it each time.
          You can still choose a different letterhead for a single batch.
        </p>
        {Object.entries(KINDS).map(([kind, k]) => (
          <div key={kind} style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 700 }}>{k.title}</div>
                <div style={{ fontSize: '0.85rem', marginTop: 2 }}>
                  {info[kind]
                    ? <>📄 <button className="btn btn-ghost btn-sm" style={{ padding: 0 }} onClick={() => download(kind)}>{info[kind].name}</button> <span style={{ color: 'var(--gray-500)' }}>· saved {info[kind].updated}</span></>
                    : <span style={{ color: 'var(--gray-500)' }}>None saved</span>}
                </div>
              </div>
              {canEdit && (
                <span style={{ display: 'flex', gap: 6 }}>
                  <input ref={refs[kind]} type="file" accept={k.accept} style={{ display: 'none' }} onChange={e => upload(kind, e.target.files[0])} />
                  <button className="btn btn-primary btn-sm" disabled={busy === kind} onClick={() => refs[kind].current?.click()}>{busy === kind ? 'Saving…' : info[kind] ? 'Replace…' : 'Upload…'}</button>
                  {info[kind] && <button className="btn btn-ghost btn-sm" disabled={busy === kind} onClick={() => remove(kind)}>Remove</button>}
                </span>
              )}
            </div>
            <p className="form-hint" style={{ marginBottom: 0 }}>{k.hint}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
